import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut,
  Search,
  Trash2,
  Mail,
  ArrowUpRight,
  RefreshCw,
  Inbox,
  CheckCircle2,
  Clock3,
  Archive,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", color: "text-indigo-300 bg-indigo-500/15 border-indigo-500/30", icon: Inbox },
  contacted: { label: "Contacted", color: "text-amber-300 bg-amber-500/15 border-amber-500/30", icon: Clock3 },
  closed: { label: "Closed", color: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30", icon: CheckCircle2 },
};

function StatusBadge({ status, onChange }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <div className="relative">
      <select
        data-testid={`lead-status-${status}`}
        value={status}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none pl-7 pr-6 py-1.5 rounded-full text-xs font-medium border ${meta.color} outline-none cursor-pointer`}
      >
        <option value="new">New</option>
        <option value="contacted">Contacted</option>
        <option value="closed">Closed</option>
      </select>
      <Icon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" />
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="glass rounded-xl p-5">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl font-light text-white">{value}</div>
    </div>
  );
}

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [inquiryFilter, setInquiryFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: s }] = await Promise.all([
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setLeads(rows);
      setStats(s);
    } catch {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && (l.status || "new") !== statusFilter) return false;
      if (inquiryFilter !== "all" && l.inquiry_type !== inquiryFilter) return false;
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.message || "").toLowerCase().includes(q)
      );
    });
  }, [leads, query, statusFilter, inquiryFilter]);

  const updateStatus = async (lead, status) => {
    const prev = leads;
    setLeads((rows) => rows.map((r) => (r.id === lead.id ? { ...r, status } : r)));
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      setLeads(prev);
      toast.error("Failed to update status.");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, { withCredentials: true });
      toast.success("Lead deleted.");
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-leads-page" className="relative min-h-screen bg-brand-bg text-white">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[400px] rounded-full bg-indigo-600/10 blur-[140px]" />

      {/* Header */}
      <header className="glass-strong border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white">
              ← Site
            </a>
            <span className="text-slate-600">/</span>
            <div className="font-display text-lg">Admin · Leads</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <div className="hidden md:flex items-center gap-2 text-sm">
              {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <span className="text-slate-300">{user?.email}</span>
            </div>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                window.location.href = "/admin/login";
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 hover:border-white/25 text-sm"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <StatCard testId="stat-total" label="Total leads" value={stats?.total ?? "—"} />
          <StatCard testId="stat-new" label="New" value={stats?.by_status?.new ?? "—"} />
          <StatCard testId="stat-contacted" label="Contacted" value={stats?.by_status?.contacted ?? "—"} />
          <StatCard testId="stat-closed" label="Closed" value={stats?.by_status?.closed ?? "—"} />
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between"
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email or message…"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg glass text-white placeholder:text-slate-500 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none"
            />
          </div>
          <div className="flex gap-2">
            <select
              data-testid="filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="glass px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-400"
            >
              <option value="all">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="closed">Closed</option>
            </select>
            <select
              data-testid="filter-inquiry"
              value={inquiryFilter}
              onChange={(e) => setInquiryFilter(e.target.value)}
              className="glass px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-400"
            >
              <option value="all">All inquiries</option>
              <option value="AI">AI</option>
              <option value="Staffing">Staffing</option>
              <option value="Both">Both</option>
            </select>
          </div>
        </motion.div>

        {/* Table */}
        <div className="mt-6 rounded-2xl glass overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/10 text-slate-400 text-xs uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Inquiry</th>
                  <th className="text-left px-5 py-3">Message</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Received</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="leads-tbody">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-slate-400">
                      <div className="mx-auto w-6 h-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-slate-400">
                      <Archive className="w-6 h-6 mx-auto text-slate-600" />
                      <div className="mt-2">No leads match your filters.</div>
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-5 py-4 font-medium text-white">{l.name}</td>
                      <td className="px-5 py-4">
                        <a
                          href={`mailto:${l.email}`}
                          className="inline-flex items-center gap-1.5 text-indigo-300 hover:text-indigo-200"
                        >
                          <Mail className="w-3.5 h-3.5" /> {l.email}
                        </a>
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2 py-1 rounded-md bg-white/5 text-slate-200 text-xs">
                          {l.inquiry_type}
                        </span>
                      </td>
                      <td className="px-5 py-4 max-w-[300px]">
                        <div className="text-slate-300 truncate" title={l.message || ""}>
                          {l.message || <span className="text-slate-600">—</span>}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge
                          status={l.status || "new"}
                          onChange={(s) => updateStatus(l, s)}
                        />
                      </td>
                      <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                        {fmtDate(l.created_at)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <a
                            href={`mailto:${l.email}?subject=${encodeURIComponent(
                              "RecruitSteps & AI — following up on your inquiry"
                            )}`}
                            data-testid={`lead-reply-${l.id}`}
                            className="p-2 rounded-md hover:bg-white/5 text-slate-300 hover:text-white"
                            title="Reply"
                          >
                            <ArrowUpRight className="w-4 h-4" />
                          </a>
                          <button
                            data-testid={`lead-delete-${l.id}`}
                            onClick={() => remove(l)}
                            className="p-2 rounded-md hover:bg-red-500/10 text-slate-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-500">
          Showing {filtered.length} of {leads.length} lead{leads.length === 1 ? "" : "s"}.
        </p>
      </main>
    </div>
  );
}
